import { useEffect, useState } from "react"
import { CSSTransition, TransitionGroup } from "react-transition-group"

import classNames from "classnames"

import { connect } from "scrivito"
import { PopoverContext, PopoverRoot } from "shared/context/PopoverContext"
import { useNavigationContext } from "shared/context/navigation/NavigationContext"
import { OverlayIDContext } from "shared/context/overlay/OverlayIDContext"
import { Overlay } from "shared/context/overlay/types"
import { useOverlayActions } from "shared/context/overlay/useOverlayActions"
import { useEvent } from "shared/lib/useEvent"
import { useFocusRestore } from "shared/lib/useFocusRestore"
import { useOverlays } from "shared/lib/useOverlays"
import { AccessibilityInfo } from "shared/ui/accessibility-info/AccessibilityInfo"

import { usePreventWindowScroll } from "features/scroll-management/model/usePreventWindowScroll"

import { OverlayFactory } from "./OverlayFactory"
import { useOverlaysWithRefs } from "./hooks/useOverlaysWithRefs"

function OverlaysComponent() {
  const { closeAllOverlays } = useOverlayActions()
  const { saveFocus, restoreFocus, clearLayer } = useFocusRestore()
  const [restoreFocusState, setRestoreFocusState] = useState<{
    shouldRestoreFocus: boolean
    layer: string | null
  }>({ shouldRestoreFocus: false, layer: null })

  const handleOverlayOpen = useEvent(
    (overlay: Overlay, previousOverlay: Overlay | null) => {
      saveFocus(overlay.options?.focusElementID, previousOverlay?.id || null)
    }
  )
  const handleOverlayClose = useEvent(
    (overlay: Overlay, nextOverlay: Overlay | null) => {
      clearLayer(overlay.id)
      setRestoreFocusState({
        shouldRestoreFocus: true,
        layer: nextOverlay?.id || null
      })
    }
  )

  const { currentOverlay, overlays, closeCurrentOverlay } = useOverlays({
    onOverlayOpen: handleOverlayOpen,
    onOverlayClose: handleOverlayClose
  })

  usePreventWindowScroll({
    id: "overlay",
    isScrollDisabled: !!currentOverlay
  })

  const handleCloseOnEscape = useEvent((event: KeyboardEvent) => {
    if (event.key === "Escape") {
      closeCurrentOverlay()
    }
  })

  useEffect(() => {
    window.addEventListener("keydown", handleCloseOnEscape)
    return () => window.removeEventListener("keydown", handleCloseOnEscape)
  }, [])

  const { subscribe } = useNavigationContext()
  useEffect(() => {
    return subscribe(() => {
      closeAllOverlays()
    })
  }, [subscribe])

  useEffect(() => {
    if (restoreFocusState.shouldRestoreFocus) {
      restoreFocus(restoreFocusState.layer)
      setRestoreFocusState({ shouldRestoreFocus: false, layer: null })
    }
  }, [restoreFocusState])

  const overlaysWithRefs = useOverlaysWithRefs(overlays)

  return (
    <PopoverContext.Provider value={PopoverRoot.OVERLAY_POPOVER_ROOT}>
      <div id={PopoverRoot.OVERLAY_POPOVER_ROOT} />
      <TransitionGroup component={null}>
        {overlaysWithRefs.map((o, index) => {
          const isTopLevelOverlay = index === overlaysWithRefs.length - 1
          return (
            <CSSTransition
              key={o.overlay.id}
              classNames="overlay"
              timeout={200}
              nodeRef={o.ref}
            >
              <OverlayIDContext.Provider value={o.overlay.id}>
                <div
                  inert={!isTopLevelOverlay}
                  ref={o.ref}
                  className={classNames("overlay", {
                    "overlay--top-level": isTopLevelOverlay
                  })}
                  role="dialog"
                  aria-modal
                  aria-labelledby={`${o.overlay.id}-title`}
                  aria-describedby="overlay-message"
                >
                  <AccessibilityInfo />
                  <OverlayFactory overlay={o.overlay} />
                </div>
              </OverlayIDContext.Provider>
            </CSSTransition>
          )
        })}
      </TransitionGroup>
    </PopoverContext.Provider>
  )
}

export const Overlays = connect(OverlaysComponent)
